<template>
  <div class="patient-photo-avatar-wrap">
    <button
      type="button"
      class="patient-photo-avatar"
      :class="{ 'is-interactive': interactive, 'is-uploading': uploading }"
      :disabled="!interactive"
      :aria-label="ariaLabel"
      data-test="patient-photo-avatar"
      @click="openSheet"
    >
      <img
        class="patient-photo-img"
        :src="displayUrl || silhouette"
        alt="Patient photo"
        @error="onImgError"
      />

      <!-- Progress ring. Deliberately a coarse 3-stage bar (picked / uploading /
           saved) rather than real byte progress: uploadToS3 POSTs through fetch(),
           which has no upload-progress event, so a "real" percentage would be a
           lie that sticks at 99%. -->
      <svg v-if="uploading" class="patient-photo-ring" viewBox="0 0 36 36" aria-hidden="true">
        <circle class="patient-photo-ring-track" cx="18" cy="18" r="16" />
        <circle
          class="patient-photo-ring-bar"
          cx="18"
          cy="18"
          r="16"
          :style="{ strokeDasharray: ringCircumference, strokeDashoffset: ringOffset }"
        />
      </svg>

      <!-- Empty/idle affordance: the WhatsApp / LinkedIn camera pip. Hidden while
           uploading so the ring is the only moving thing. -->
      <span v-if="showBadge" class="patient-photo-badge" data-test="patient-photo-badge">
        <i class="fa fa-camera"></i>
      </span>
    </button>

    <!-- The camera/library inputs, the in-tree sheet and its phone bottom-sheet
         styles live in PhotoActionSheet (shared with "Photo to OD"). This component
         adds only the rows that belong to a PROFILE photo: View and Remove. -->
    <PhotoActionSheet
      v-model="sheetOpen"
      title="Patient photo"
      test-id="patient-photo"
      :show-take="isTouch"
      @file="onFileChosen"
    >
      <button
        v-if="hasPhoto"
        type="button"
        class="photo-sheet-row"
        data-test="patient-photo-view"
        @click="viewPhoto"
      >
        <i class="fa fa-search-plus"></i><span>View photo</span>
      </button>

      <button
        v-if="hasPhoto"
        type="button"
        class="photo-sheet-row text-danger"
        data-test="patient-photo-remove"
        @click="onRemove"
      >
        <i class="fa fa-trash-o"></i><span>Remove photo</span>
      </button>
    </PhotoActionSheet>

    <b-modal
      v-model="viewerOpen"
      static
      lazy
      hide-header
      hide-footer
      centered
      size="md"
      class="patient-photo-modal-root"
      data-test="patient-photo-viewer"
    >
      <img :src="displayUrl" class="patient-photo-viewer-img" alt="Patient photo" />
    </b-modal>
  </div>
</template>

<script>
import silhouette from '@/assets/people/icon-person.png';
import { maskState } from '@/utils/phiMaskState';
import { extractApiError } from '@/utils/apiError';
// Is there a touch screen to point a camera with? Shared with the header search's camera
// shortcut, so that shortcut appears exactly where this sheet offers "Take photo".
import { hasTouchScreen } from '@/utils/deviceType';
import { photoSheetRequest, takePhotoSheetRequest } from '@/utils/photoSheetRequest';
import PhotoActionSheet from './PhotoActionSheet.vue';

/**
 * The round photo in the Patient Profile header.
 *
 * WHAT IT IS: the whole circle is one tap target (48px desktop, 72px at ≤768px —
 * this is a phone/tablet feature first). Tapping opens OUR OWN action sheet rather
 * than the browser's file dialog, because the file dialog cannot offer "view" or
 * "remove", and on Android it buries the camera two taps deep.
 *
 * SAVE IS IMMEDIATE. There is no in-app "is this one OK?" step: the phone's camera
 * app already asked that (Retake / Use Photo), and a second confirmation is where
 * front-desk staff abandon the flow. "Remove photo" clears the TEAMIO copy only — it
 * is not an undo of the Open Dental copy: every save hands the exe its own transit
 * copy, so a photo removed seconds after it was taken still files into OD
 * (2026-09-24: removing the object the exe was about to read stalled the office's
 * whole Open Dental sync queue).
 *
 * ⛔ DEMO MASK. A patient's face is the least maskable PHI there is — no
 * deterministic fake can stand in for it — so when the mask is on this renders the
 * silhouette, drops the camera badge, and refuses to open the sheet. That check is
 * `masked`, read from the reactive phiMaskState, NOT from allSettings: the per-user
 * header toggle never touches settings. See CLAUDE.md → "Demo mask".
 *
 * The parent owns the gate (`enabled`) and the identity (`siteId`/`patNum`); this
 * component owns everything else and tells the parent what changed via `updated` /
 * `removed`.
 */
export default {
  name: 'PatientPhotoAvatar',

  components: { PhotoActionSheet },

  props: {
    siteId: { type: String, required: true },
    patNum: { type: [String, Number], required: true },
    enabled: { type: Boolean, default: false },
  },

  data() {
    return {
      // Bundled asset, not a URL from the server — so it can never 404 and never
      // carries PHI. Held in data only because the template needs it.
      silhouette,
      photoUrl: null,
      // Local object URL shown while the upload is in flight, so the new face
      // appears the instant it is picked instead of after the round trip.
      previewUrl: null,
      loading: false,
      uploading: false,
      stage: 0,
      sheetOpen: false,
      viewerOpen: false,
      // One retry only — see onImgError().
      reloadedOnce: false,
      // `capture` is meaningless on a desktop mouse, and a "Take photo" row that
      // opens a file browser is worse than no row.
      // Seeded here and re-read in openSheet(): automation (Playwright's touch emulation)
      // attaches touch AFTER the document starts, and a creation-time read alone would never
      // see it. Real devices report it from the first byte either way.
      isTouch: hasTouchScreen(),
      // 2πr for the r=16 circle in the 36x36 viewBox.
      ringCircumference: 100.53,
    };
  },

  computed: {
    masked() {
      const m = maskState();
      return !!(m && (m.userMask || m.siteMask));
    },

    displayUrl() {
      return !this.masked && (this.previewUrl || this.photoUrl);
    },

    interactive() {
      return this.enabled && !this.masked;
    },

    showBadge() {
      return this.interactive && !this.uploading;
    },

    hasPhoto() {
      return !!this.photoUrl;
    },

    ringOffset() {
      const pct = Math.max(0, Math.min(100, this.stage));
      return this.ringCircumference * (1 - pct / 100);
    },

    ariaLabel() {
      if (!this.interactive) return 'Patient photo';
      return this.hasPhoto
        ? 'Patient photo — take a new photo, view or remove'
        : 'Add a patient photo';
    },

    // Bumps whenever the header search's camera shortcut asks for this sheet
    // (utils/photoSheetRequest.js) — including for the patient already on screen,
    // where there is no navigation to re-run the patNum watcher.
    pendingPhotoSheetAt() {
      return photoSheetRequest.at;
    },
  },

  watch: {
    // The profile is reused across patients (family-member links change the route
    // param without remounting), so the old face MUST be cleared synchronously —
    // waiting for the fetch would show patient A's photo over patient B's name.
    patNum: {
      immediate: true,
      handler() {
        this.photoUrl = null;
        this.previewUrl = null;
        this.reloadedOnce = false;
        this.load();
        this.consumePhotoSheetRequest();
      },
    },
    enabled(v) {
      if (v) this.load();
    },
    // Settings can arrive after the profile mounts; a pending request waits for them.
    interactive(v) {
      if (v) this.consumePhotoSheetRequest();
    },
    pendingPhotoSheetAt() {
      this.consumePhotoSheetRequest();
    },
  },

  methods: {
    async load() {
      if (!this.enabled) {
        this.photoUrl = null;
        return;
      }
      this.loading = true;
      try {
        const data = await this.$store.dispatch('patientPhoto/load', {
          siteID: this.siteId,
          patNum: this.patNum,
        });
        this.photoUrl = data && data.url ? data.url : null;
      } catch (err) {
        // NEVER toast here. This fires on every profile open; a site with a
        // transient backend blip would greet staff with a red error on a feature
        // they did not ask for. The silhouette is a perfectly good fallback.
        console.warn('[PatientPhotoAvatar] could not load the patient photo:', err);
        this.photoUrl = null;
      } finally {
        this.loading = false;
      }
    },

    openSheet() {
      // Guard, not just :disabled — a disabled button still fires programmatic
      // click events, and this is the masked/not-entitled boundary.
      if (!this.interactive) return;
      this.isTouch = hasTouchScreen();
      this.sheetOpen = true;
    },

    /**
     * The header search's camera shortcut asked for this patient's sheet. Opens the
     * SHEET only — "Take photo" stays the user's own tap, because iOS Safari ignores
     * a programmatic file-input click that is not inside a user gesture.
     */
    consumePhotoSheetRequest() {
      if (!this.interactive) return;
      if (takePhotoSheetRequest(this.patNum)) this.openSheet();
    },

    viewPhoto() {
      this.sheetOpen = false;
      this.viewerOpen = true;
    },

    // PhotoActionSheet emits the picked File (and has already reset its input).
    async onFileChosen(file) {
      if (!file) return;

      const prev = this.photoUrl;
      const objectUrl = URL.createObjectURL(file);
      this.previewUrl = objectUrl;
      this.uploading = true;
      this.stage = 15;

      try {
        this.stage = 40;
        const data = await this.$store.dispatch('patientPhoto/upload', {
          siteID: this.siteId,
          patNum: this.patNum,
          file,
        });
        this.stage = 100;
        this.photoUrl = data.url;

        // The OD copy is queued separately and can be skipped (no image category
        // configured, exe down). Saying so is the difference between "it worked"
        // and a chart that silently never gets the photo.
        if (data.odStatus === 'skipped') {
          this.$toast.warning(
            'Photo saved in Teamio, but not sent to Open Dental: '
            + (data.odReason || 'no image category configured.')
          );
        } else {
          // "Sending", not "sent": the save only queues the OD copy — the office's
          // connector app files it on its next poll (about a minute).
          this.$toast.success('Photo saved. Sending to Open Dental…');
        }
        this.$emit('updated', { url: data.url });
      } catch (err) {
        // Put the old face back. Leaving the preview up after a failure is the
        // worst outcome: the screen says saved, the server disagrees.
        this.photoUrl = prev;
        this.$toast.error(extractApiError(err, 'Could not save the photo. Please try again.'));
      } finally {
        try { URL.revokeObjectURL(objectUrl); } catch (revokeErr) { /* nothing to release */ }
        this.previewUrl = null;
        this.uploading = false;
        this.stage = 0;
      }
    },

    async onRemove() {
      this.sheetOpen = false;
      try {
        const ok = await this.$bvModal.msgBoxConfirm(
          'Remove this patient photo from Teamio? Open Dental keeps any copy already filed — including one still on its way.',
          { okTitle: 'Remove', okVariant: 'danger', centered: true }
        );
        if (!ok) return;

        await this.$store.dispatch('patientPhoto/remove', {
          siteID: this.siteId,
          patNum: this.patNum,
        });
        this.photoUrl = null;
        this.previewUrl = null;
        this.reloadedOnce = false;
        this.$toast.success('Photo removed.');
        this.$emit('removed');
      } catch (err) {
        this.$toast.error(extractApiError(err, 'Could not remove the photo. Please try again.'));
      }
    },

    onImgError() {
      // The backend hands back a presigned URL that expires in an hour, so a
      // profile left open on a treatment-room screen overnight gets a 403 on the
      // <img>. Refetch ONCE — a second failure means the object is really gone,
      // and looping would hammer the endpoint.
      if ((this.previewUrl || this.photoUrl) && !this.reloadedOnce) {
        this.reloadedOnce = true;
        this.load();
        return;
      }
      this.photoUrl = null;
      this.previewUrl = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-photo-avatar-wrap {
  display: inline-block;
  line-height: 0;
}

.patient-photo-avatar {
  position: relative;
  display: block;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: default;

  &.is-interactive {
    cursor: pointer;
  }

  // The circle is the patient's photo, not a greyed-out control: a site without
  // the feature should look normal, not broken.
  &:disabled {
    opacity: 1;
  }
}

.patient-photo-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.4);
  background: white;
  padding: 2px;
  display: block;
}

.patient-photo-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  // SVG strokes start at 3 o'clock; a progress ring has to start at 12.
  transform: rotate(-90deg);
  pointer-events: none;

  circle {
    fill: none;
    stroke-width: 3;
  }
}

.patient-photo-ring-track {
  stroke: rgba(255, 255, 255, 0.35);
}

.patient-photo-ring-bar {
  stroke: #ffffff;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease;
}

.patient-photo-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  color: #0f7259;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

// ---------------------------------------------------------------------------
// The photo VIEWER. (The action sheet, its rows and its phone bottom-sheet rules
// live in PhotoActionSheet.vue.) `::v-deep` stays UNDER .patient-photo-avatar-wrap
// so the rules can never leak to another page's modal; this only works because
// the viewer b-modal is `static` (in-tree).
// ---------------------------------------------------------------------------
.patient-photo-avatar-wrap {
  // Same lift as the sheet, for the same reason: the help bot (z-index 9999) would
  // otherwise sit over the enlarged photo. See PhotoActionSheet.vue for why the lever
  // is the b-modal's root element and needs !important.
  ::v-deep .patient-photo-modal-root {
    z-index: 10050 !important;
  }

  ::v-deep .patient-photo-viewer-img {
    width: 100%;
    height: auto;
    max-height: 80vh;
    object-fit: contain;
  }
}

@media (max-width: 768px) {
  .patient-photo-avatar {
    width: 72px;
    height: 72px;
  }

  .patient-photo-badge {
    width: 26px;
    height: 26px;
    font-size: 13px;
    right: 0;
    bottom: 0;
  }
}
</style>
